import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { Breadcrumb, Button, Form, Modal, OverlayTrigger, Table } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiChevronRight, BiCopy, BiPencil, BiPlus, BiPlusCircle, BiSearch, BiSolidUserPlus, BiTrash } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { EventContext, LoaderContext } from '../../../../App';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import Pagination from "../../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, EventType, LS_AUTHORITY_KEY, LS_SELECTED_MAT_ISSUE_YEAR, currentYear, startYear } from '../../../../core/constants/common';
import StakehodlerLibraryService from '../../../../core/service/StakeholderLibraryService';
import Utils from '../../../../core/utilities/Utils';
import assessment_pic from "../../../../images/Assessment.png";
import './MaterialityAssessment.scss';

const StakeholdersDefaultView = ({ t }) => {
    // All hooks must be called first, in the same order every render
    const { setLoaderDisplay } = useContext(LoaderContext);
    const navigate = useNavigate();
    const [filter, setFilter] = useState(() => {
        const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;
        const year = new Date().getFullYear();
        return {
            year: selectedYear ?? `${year}`,
        };
    });
    const [showAddStakeholder, setShowAddStakeholder] = useState(false);
    const [validated, setValidated] = useState(false);
    const [stakeholderDetails, setStakeholderDetails] = useState([
        {
            id: '',
            name: '',
            email: '',
            categoryId: '',
            groupId: '',
            groups: [],
            isDataAvailable: false
        }
    ]);
    const [stakeholders, setStakeholders] = useState([]);
    const [itemsTotalCount, setItemTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedStakeholder, setSelectedStakeholder] = useState({ id: null });
    const [searchValue, setSearchValue] = useState("");
	const { setEvent } = useContext(EventContext);
    
    useEffect(() => {
        localStorage.setItem(LS_SELECTED_MAT_ISSUE_YEAR, filter.year);
        getStakeHolders(itemsPerPage, currentPage);
    }, [filter]);

    // Non-hook variables after all hooks
    const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
    const companyId = lsDat.profile.company.id;
    const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;
    const year = new Date().getFullYear();
    const yearOptions = [];
    for (let i = currentYear; i >= startYear; i--) {
        yearOptions.push(<option value={i} key={i}>{i}</option>);
    }

    const getStakeHolders = (pageSize, pageNumber) => {
        if (pageNumber > 0) {
            pageNumber = pageNumber - 1;
        } else {
            setCurrentPage(1);
        }
        const perPage = pageSize > 0 ? pageSize : itemsPerPage;

        setLoaderDisplay(true);
        StakehodlerLibraryService.getAllStakeholdersDefaultView(`${filter.year}`, companyId, perPage, pageNumber,searchValue)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;

                setItemTotalCount(parseFloat(res.headers["x-total-count"]));
                setStakeholders(res.data);
            })
            .catch(err => {
                setLoaderDisplay(false);
            });
    };

    const getAllCategories = () => {
        setLoaderDisplay(true);
        StakehodlerLibraryService.getAllStakeholders(`${filter.year}`, companyId)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;

                setCategories(res.data?.sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(err => {
                setLoaderDisplay(false);
            });
    }

    const getCategoryWiseGroups = async (categoryId) => {
        setLoaderDisplay(true);
        try {
            const res = await StakehodlerLibraryService.getStakeholderDet(categoryId);
            setLoaderDisplay(false);

            if (res.status !== 200) return [];

            return res.data?.children?.sort((a, b) => a.name.localeCompare(b.name)) || [];
        } catch (err) {
            setLoaderDisplay(false);
            console.error("Error fetching groups:", err);
            return [];
        }
    };


    const addOrEditStakeholder = async (item, type) => {
        setShowAddStakeholder(true);
        if (type === 'Edit') {
            const categoryGroups = await getCategoryWiseGroups(item.categoryId);
            setStakeholderDetails([
                {
                    id: item.id,
                    name: item.name,
                    email: item.email,
                    categoryId: item.categoryId,
                    groupId: item.groupId,
                    groups: categoryGroups,
                    isDataAvailable: true
                }
            ]);
            setSelectedStakeholder({ id: item.groupId });
        }
        getAllCategories();
    }

    const handleAddStakeholderClose = () => {
        setShowAddStakeholder(false);
        setStakeholderDetails([
            {
                id: '',
                name: '',
                email: '',
                categoryId: '',
                groupId: '',
                groups: []
            }
        ]);
        setCategories([]);
        setValidated(false);
        selectedStakeholder({ id: null });
    };

    const handleAddFields = () => {
        setStakeholderDetails([
            ...stakeholderDetails,
            {
                id: '',
                name: '',
                email: '',
                categoryId: '',
                groupId: '',
                groups: [],
                isDataAvailable: false
            }
        ]);
        setValidated(false);
    };

    const preparePayload = () => {
        const payload = stakeholderDetails.map(({ id, name, email, categoryId, groupId }) => ({
            id,
            name,
            email,
            categoryId,
            groupId,
        }));
        return payload;
    };


    const upsertStakeholders = (e) => {
        e.preventDefault();
        if (stakeholderDetails.some(stakeholder =>
            !stakeholder.name ||
            !stakeholder.email ||
            !stakeholder.categoryId ||
            !stakeholder.groupId)) {
            setValidated(true);
            return;
        }

        const payload = preparePayload();
        const stakeholderId = selectedStakeholder.id
            ? selectedStakeholder.id
            : stakeholderDetails[0]?.groupId;

        setLoaderDisplay(true);
        StakehodlerLibraryService.upsertMultipleStakeholders(companyId, payload)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;

                toast.success('Data added successfully', {
                    position: toast.POSITION.TOP_RIGHT,
                });

                getStakeHolders(itemsPerPage, currentPage);
                handleAddStakeholderClose();
                setSelectedStakeholder({ id: null });
            })
            .catch(err => {
                setLoaderDisplay(false);
            });
    }

    const showDeleteConfirm = (stakeholder) => {
        setShowDeleteConfirmation(true);
        setSelectedStakeholder({ id: stakeholder.id });
    }

    const confirmDelete = (val) => {
        if (val) {
            deleteStakeHolder(selectedStakeholder.id);
        }
        setShowDeleteConfirmation(false);
    };

    const deleteStakeHolder = (id) => {
        setLoaderDisplay(true);
        StakehodlerLibraryService.deleteStakeholderGroupEmail(id)
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 204) return;

                toast.success('Stakeholder deleted successfully', {
                    position: toast.POSITION.TOP_RIGHT,
                });
                getStakeHolders(itemsPerPage, currentPage);
            }).catch(err => {
                setLoaderDisplay(false);
            });
    };

    const handleChange = async (index, event) => {
        const { name, value } = event.target;
        const updatedStakeholders = [...stakeholderDetails];

        updatedStakeholders[index][name] = value;

        if (name === 'categoryId') {
            try {
                const categoryGroups = await getCategoryWiseGroups(value);
                updatedStakeholders[index].groups = categoryGroups;
                updatedStakeholders[index].groupId = '';
            } catch (error) {
                console.error("Error fetching groups:", error);
            }
        }
        setStakeholderDetails(updatedStakeholders);
    };

    const handleDelete = (index) => {
        const newFormFields = stakeholderDetails.filter((_, i) => i !== index);
        setStakeholderDetails(newFormFields);
    }

    const blankCardToRender = () => {
		return (
			<div className="Assessment_blankcard">
				<div>
					<div className="assessment_pic">
						<img src={assessment_pic} />
					</div>
					<h4 className="mb-4">{t('StakeholderBlankHeader.label')}</h4>
					<Button variant="primary" className="px-3" onClick={() => addOrEditStakeholder(null, 'Add')}>
						<BiPlus className="mr-2" /> {t("New.label")}
					</Button>
				</div>
			</div>
		);
	};

    const renderStakeholders = () => {
        return (
            <div className='Stakeholders_table_01'>
                <Table className='m-0'>
                    <thead>
                        <tr>
                            <th>{t("Name.label")}</th>
                            <th>{t("Emailid.label")}</th>
                            <th>{t("Type.label")}</th>
                            <th>{t("Category.label")}</th>
                            <th>{t("Group.label")}</th>
                            <th>{t("CreatedOn.label")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            stakeholders?.map((stakeholder, index) => {
                                return (
                                    <tr className='MainHolder' id={`MainHolder-${index}`} key={`MainHolder-${index}`}>
                                        <td>{stakeholder?.name}</td>
                                        <td>{stakeholder?.email}</td>
                                        <td>{stakeholder?.type === 'INTERNAL' ? 'Internal' : stakeholder?.type === 'EXTERNAL' ? 'External' : ''}</td>
                                        <td>{stakeholder?.category}</td>
                                        <td>{stakeholder?.groupName}</td>
                                        <td>{moment(stakeholder?.createdDate).format("DD MMM YYYY, h:mm A")}</td>
                                        <td>

                                            {/* <Dropdown>
                                                <Dropdown.Toggle variant="blank_outline">
                                                    <BiDotsHorizontalRounded />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => addOrEditStakeholder(stakeholder, 'Edit')}>  <BiPencil /> {t("Edit.label")}</Dropdown.Item>
                                                    <Dropdown.Item onClick={() => showDeleteConfirm(stakeholder)}>   <BiTrash /> {t("Delete.label")}</Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown> */}

                                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Edit.label"))}>
                                                <button className='btn btn-blank_outline' onClick={() => addOrEditStakeholder(stakeholder, 'Edit')}>
                                                    <BiPencil />
                                                </button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("Delete.label"))}>
                                                <button className='btn btn-blank_outline' onClick={() => showDeleteConfirm(stakeholder)}>
                                                    <BiTrash />
                                                </button>
                                            </OverlayTrigger>

                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </Table>
            </div>
        );
    }

    const renderAddStakeholderModal = () => {
        return (
            <Modal show={showAddStakeholder} onHide={() => { handleAddStakeholderClose(); }}
                size='xl'
                aria-labelledby="contained-modal-title-vcenter"
                centered
                backdrop="static"
                keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title as="h5">{!stakeholderDetails[0]?.isDataAvailable ? t("Addstakeholder.label") : t("EditStakeholder.label")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validated}>

                        <table className='table AddStakeholder_01A'>
                            <thead>
                                <tr>
                                    <th>{t("Name.label")} <span className="text-danger p-0">*</span></th>
                                    <th> {t("Email.label")} <span className="text-danger p-0">*</span></th>
                                    <th>{t("Category.label")} <span className="text-danger">*</span></th>
                                    <th>{t("Group.label")} <span className="text-danger">*</span></th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {stakeholderDetails.map((stakeholder, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                // placeholder="Name"
                                                value={stakeholder.name}
                                                name="name"
                                                onChange={(e) => handleChange(index, e)}
                                                required
                                                maxLength={250}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                // placeholder="Email"
                                                value={stakeholder.email}
                                                name="email"
                                                onChange={(e) => handleChange(index, e)}
                                                required
                                                maxLength={250}
                                            />
                                        </td>
                                        <td className='SH_Category-td'>
                                            <select
                                                className='form-select'
                                                name="categoryId"
                                                required
                                                value={stakeholder.categoryId}
                                                onChange={(e) => handleChange(index, e)}
                                            >
                                                <option value='' key='' disabled>{t('Select.label')}</option>
                                                {categories?.map((e) => (
                                                    <option value={e.id} key={e.id}>{e.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className='SH_Group-td'>
                                            <select
                                                className='form-select'
                                                name="groupId"
                                                required
                                                value={stakeholder.groupId}
                                                onChange={(e) => handleChange(index, e)}
                                                disabled={!stakeholder.categoryId || !Array.isArray(stakeholder.groups)}
                                            >
                                                <option value='' key='' disabled>{t('Select.label')}</option>
                                                {stakeholder.groups?.map((e) => (
                                                    <option value={e.id} key={e.id}>{e.name}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            {
                                                stakeholderDetails.length > 1 &&
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={Utils.renderTooltip(
                                                        'Delete'
                                                    )}>
                                                    <Button variant='GroupStake'  onClick={() => handleDelete(index)}>
                                                        <BiTrash />
                                                    </Button>
                                                </OverlayTrigger>
                                            }
                                        </td>
                                        <td>
                                            {
                                                stakeholderDetails.length - 1 === index && !stakeholder.isDataAvailable &&
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={Utils.renderTooltip(
                                                        'Add new'
                                                    )}>
                                                    <Button variant='GroupStake' className='btn-outline-primary border-0' onClick={handleAddFields}>
                                                        <BiPlusCircle />
                                                    </Button>
                                                </OverlayTrigger>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className='p-2 pt-3 text-center'>
                            <Button variant="secondary" className='px-4 me-2' onClick={handleAddStakeholderClose}>
                                {t("Close.label")}
                            </Button>
                            {
                                <Button variant="primary" className='px-4' onClick={(e) => upsertStakeholders(e)}>
                                    {t("Save.label")}
                                </Button>
                            }
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        );
    };

    const handleSearch = (e) => {
        setCurrentPage(1);
        getStakeHolders(itemsPerPage, 0);
    }

    return (
        <>
            <div className='StakeholdersBox'>
                <div className="List_header pb-0">
                    <Breadcrumb className='h5'>
                        <Breadcrumb.Item onClick={() => navigate(`/company_admin/dmat/stats`)}>{t("Materiality.label")}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t("StakeHolders.label")}</Breadcrumb.Item>
                    </Breadcrumb>

                    <div className='d-flex justify-content-end align-items-center header-actions'>
                        <div className='searchListBox'>
                            <span><BiSearch /></span>
                            <input type='text' className='form-control ' placeholder={t("Search.label")} 
                             onChange={(e) => setSearchValue(e.target.value)}
                             onKeyDown={(e) => {if (e.key === "Enter") {
                                    handleSearch(e)
                                }}}/>
                        </div>
                        <div className="StakeholdersCheckCard">
                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('GroupViewTooltip.label'))}>
                                <div className="toggle-switch">
                                    <Form.Check
                                        inline
                                        label={<BiSolidUserPlus />}
                                        type="radio"
                                        id="viewMode1"
                                        value={'GRID'}
                                        onClick={() => navigate(`/company_admin/dmat/stakeholders`)}
                                    />
                                </div>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('ListViewTooltip.label'))}>
                                <div className="toggle-switch">
                                    <Form.Check
                                        inline
                                        label={<BiCopy />}
                                        type="radio"
                                        id="viewMode2"
                                        value={'LIST'}
                                        checked
                                    />
                                </div>
                            </OverlayTrigger>
                        </div>
                        {/* <button className="btn btn-headbtn" >
                            <BsFilter />
                        </button> */}
                        <select className='form-select w-auto '
                            onChange={(e) => {
                                setFilter({ ...filter, year: e.target.value });
                                setEvent({
                                    event: EventType.DOUBLE_MAT_YEAR_CHANGED,
                                    data: { year: e.target.value},
                                });
                            }}
                            value={filter.year} >
                            {yearOptions}
                        </select>
                        {
                            stakeholders.length > 0 &&
                            <Button variant='primary' className='white-space' onClick={() => addOrEditStakeholder(null, 'Add')}><BiPlus /> {t("New.label")}</Button>
                        }
                        <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.MatIssues"))}>
                            <Button variant='secondary' className='btn  next-btn' onClick={() => navigate(`/company_admin/dmat/issues`)}>
                                <span>{t("Next.label")}</span> <BiChevronRight />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
                <div>
                    {t('MaterialityDefaultTitle.label')}
                </div>
                {stakeholders.length === 0 && blankCardToRender()}
                {stakeholders.length > 0 && renderStakeholders()}
                {
                    stakeholders?.length > 0 &&
                    <div className="p-2 List_footer_count">
                        <Pagination
                            itemsTotalCount={itemsTotalCount}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            alwaysShown={true}
                            setItemsPerPage={setItemsPerPage}
                            pageClicked={(pageNum, perPage) => {
                                getStakeHolders(perPage, pageNum);
                            }}
                        />
                    </div>
                }
            </div>
            {renderAddStakeholderModal()}
            {
                showDeleteConfirmation &&
                <ConfirmationModal
                    modalShow={true}
                    messageText={t("ConfirmDelete.label")}
                    callbackModal={confirmDelete}
                    confirmBtn={t("Confirm.label")}
                    CancelBtn={t("Cancel.label")}
                />
            }
        </>
    );

};

export default withTranslation()(StakeholdersDefaultView);